import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { NavController, NavParams, ViewController } from "ionic-angular";
import { Order, OrderComponent } from "models/order-management/models";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

@Component({
  selector: "quantity",
  templateUrl: "quantity.html",
})
export class Quantity implements OrderComponentInterface, OnInit {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() get orderFormState(): OrderFormState {
    return this._orderFormState;
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  quantity: number | undefined = undefined;
  min: number;
  max: number;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService
  ) {}
  private _orderFormState: OrderFormState;

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    const property = this.orderComponent.properties[0];
    this.min = property.min;
    this.max = property.max;
    if (this.shouldShow) {
      this.quantity = property.min;
      this.onNumberInputValueChange();
    }
  }

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  public get shouldShow() {
    return this._orderFormState.PRN;
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  set orderFormState(orderFormState: OrderFormState) {
    const componentField = this.orderComponent.properties[0].name;
    const shouldClear = OrderFormState.shouldClear(
      componentField,
      this._orderFormState,
      orderFormState
    );
    this._orderFormState = orderFormState;

    if (shouldClear) {
      this.quantity = undefined;
      const updateObject: OrderCreateRequestUpdate = { updates: {} };
      updateObject.deletes = [componentField as keyof Order];
      this.orderCreateRequestEvent.emit(updateObject);
      this.orderFormStateEvent.emit({
        PRNQuantity: this.quantity,
      });
    }

    if (this.shouldShow && !this.quantity) {
      this.quantity = this.orderComponent.properties[0].min;
      this.onNumberInputValueChange();
    }
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/

  onItemClear() {
    const componentField = this.orderComponent.properties[0].name;
    this.quantity = undefined;
    this.orderCreateRequestEvent.emit({
      deletes: [componentField as keyof Order],
    });
  }

  onNumberInputValueChange() {
    const updateObject: OrderCreateRequestUpdate = { updates: {} };
    const componentField = this.orderComponent.properties[0].name;
    updateObject.updates[componentField] = this.quantity;
    this.orderCreateRequestEvent.emit(updateObject);
    this.orderFormStateEvent.emit({
      PRNQuantity: this.quantity,
    });
  }

  isRequired() {
    return true; // not currently possible to not select a value
  }

  onUpArrowClicked() {
    if (this.quantity < this.max) {
      this.quantity++;
      this.onNumberInputValueChange();
    }
  }

  onDownArrowClicked() {
    if (this.quantity > this.min) {
      this.quantity--;
      this.onNumberInputValueChange();
    }
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "") // Trim leading and trailing whitespace
        .replace(/([a-z])([A-Z])/g, "$1-$2") // Add hyphen between camelCase words
        .replace(/[\s_]+/g, "-") // Replace spaces and underscores with hyphens
        .replace(/[^\w-]+/g, "") // Remove punctuation (non-word characters except hyphen)
        .toLowerCase(); // Convert to lowercase
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/quantity/quantity.ts